import { DollarSign, TrendingUp, CreditCard, AlertTriangle } from 'lucide-react';
import { founderAnalyticsService } from '../../lib/founder-analytics-service';
import { comparisonUtils } from '../../lib/comparison-utils';

export function RevenueTab({ data }: { data: any }) {
  if (!data || !data.mrr) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No revenue data available</p>
      </div>
    );
  }

  const { mrr, revenue, churn } = data;

  const weekOverWeekMRR = comparisonUtils.getWeekOverWeekComparison(
    mrr.current_mrr,
    mrr.last_week_mrr || 0
  );

  const monthOverMonthMRR = comparisonUtils.getMonthOverMonthComparison(
    mrr.current_mrr,
    mrr.last_month_mrr || 0
  );

  const annualRunRate = mrr.current_mrr * 12;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-[#8CAFA8] to-[#7A9B94] p-6 rounded-[16px] text-white shadow-[0_2px_12px_rgba(140,175,168,0.3)]">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign size={24} />
            <h3 className="text-sm font-medium opacity-90">Current MRR</h3>
          </div>
          <div className="text-4xl font-bold mb-2">
            {founderAnalyticsService.formatCurrency(mrr.current_mrr)}
          </div>
          <p className="text-sm opacity-75">Monthly Recurring Revenue</p>
        </div>

        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="text-[#8CAFA8]" size={24} />
            <h3 className="text-sm font-medium text-[#6B7280]">Annual Run Rate</h3>
          </div>
          <div className="text-4xl font-bold text-[#1F2937] mb-2">
            {founderAnalyticsService.formatCurrency(annualRunRate)}
          </div>
          <div className="text-sm text-[#6B7280]">Based on current MRR</div>
        </div>

        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3 mb-2">
            <CreditCard className="text-[#8CAFA8]" size={24} />
            <h3 className="text-sm font-medium text-[#6B7280]">Active Subscriptions</h3>
          </div>
          <div className="text-4xl font-bold text-[#1F2937] mb-2">
            {mrr.subscription_count || 0}
          </div>
          <div className="text-sm text-[#6B7280]">Paying customers</div>
        </div>

        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="text-red-500" size={24} />
            <h3 className="text-sm font-medium text-[#6B7280]">Churn Rate</h3>
          </div>
          <div className="text-4xl font-bold text-[#1F2937] mb-2">
            {churn?.churn_rate_30d?.toFixed(1) || 0}%
          </div>
          <div className="text-sm text-[#6B7280]">Last 30 days</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-4">MRR Growth (WoW)</h3>
          <div className="space-y-4">
            <div className="p-4 bg-[#F9FAFB] rounded-[12px]">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-[#6B7280]">This Week</span>
                <span className="text-2xl font-bold text-[#1F2937]">
                  {founderAnalyticsService.formatCurrency(mrr.current_mrr)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#6B7280]">Last Week</span>
                <span className="text-2xl font-bold text-[#6B7280]">
                  {founderAnalyticsService.formatCurrency(mrr.last_week_mrr || 0)}
                </span>
              </div>
            </div>
            <div className={`p-4 rounded-[12px] ${comparisonUtils.getTrendBackgroundColor(weekOverWeekMRR.isPositive)}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Growth</span>
                <span className={`text-xl font-bold ${comparisonUtils.getTrendColor(weekOverWeekMRR.isPositive)}`}>
                  {founderAnalyticsService.formatCurrency(weekOverWeekMRR.change)}
                </span>
              </div>
              <div className={`text-sm mt-1 ${comparisonUtils.getTrendColor(weekOverWeekMRR.isPositive)}`}>
                {comparisonUtils.formatChangePercent(weekOverWeekMRR)} change
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-4">MRR Growth (MoM)</h3>
          <div className="space-y-4">
            <div className="p-4 bg-[#F9FAFB] rounded-[12px]">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-[#6B7280]">This Month</span>
                <span className="text-2xl font-bold text-[#1F2937]">
                  {founderAnalyticsService.formatCurrency(mrr.current_mrr)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#6B7280]">Last Month</span>
                <span className="text-2xl font-bold text-[#6B7280]">
                  {founderAnalyticsService.formatCurrency(mrr.last_month_mrr || 0)}
                </span>
              </div>
            </div>
            <div className={`p-4 rounded-[12px] ${comparisonUtils.getTrendBackgroundColor(monthOverMonthMRR.isPositive)}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Growth</span>
                <span className={`text-xl font-bold ${comparisonUtils.getTrendColor(monthOverMonthMRR.isPositive)}`}>
                  {founderAnalyticsService.formatCurrency(monthOverMonthMRR.change)}
                </span>
              </div>
              <div className={`text-sm mt-1 ${comparisonUtils.getTrendColor(monthOverMonthMRR.isPositive)}`}>
                {comparisonUtils.formatChangePercent(monthOverMonthMRR)} change
              </div>
            </div>
          </div>
        </div>
      </div>

      {revenue && revenue.length > 0 && (
        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-4">Revenue Breakdown by Plan</h3>
          <div className="space-y-3">
            {revenue.map((plan: any) => (
              <div key={plan.plan_type} className="p-4 bg-[#F9FAFB] rounded-[12px]">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-[#1F2937] capitalize">{plan.plan_type}</h4>
                    <p className="text-sm text-[#6B7280]">{plan.subscription_count} subscriptions</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-[#1F2937]">
                      {founderAnalyticsService.formatCurrency(plan.total_revenue)}
                    </div>
                    <div className="text-sm text-[#6B7280]">{plan.percentage_of_total}% of total</div>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div
                    className="bg-[#8CAFA8] h-2 rounded-full transition-all"
                    style={{ width: `${plan.percentage_of_total}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {churn && (
        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-4">Churn Analytics</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-[#D1FAE5] rounded-[12px] border border-[#A7F3D0]">
              <div className="text-sm text-[#065F46] mb-1">Active Subscriptions</div>
              <div className="text-3xl font-bold text-[#065F46]">{churn.active_subscriptions}</div>
            </div>
            <div className="p-4 bg-red-50 rounded-[12px] border border-red-200">
              <div className="text-sm text-red-700 mb-1">Churned (30d)</div>
              <div className="text-3xl font-bold text-red-700">{churn.churned_30d}</div>
            </div>
            <div className="p-4 bg-orange-50 rounded-[12px] border border-orange-200">
              <div className="text-sm text-orange-700 mb-1">Churn Rate</div>
              <div className="text-3xl font-bold text-orange-700">{churn.churn_rate_30d}%</div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-[#E8F2F0] border border-[#8CAFA8]/30 p-6 rounded-[16px]">
        <h4 className="font-medium text-[#1F2937] mb-2">Revenue Insights</h4>
        <ul className="space-y-2 text-sm text-[#374151]">
          {weekOverWeekMRR.isPositive ? (
            <li>MRR is growing week-over-week ({comparisonUtils.formatChangePercent(weekOverWeekMRR)})</li>
          ) : (
            <li>MRR has declined compared to last week ({comparisonUtils.formatChangePercent(weekOverWeekMRR)})</li>
          )}
          {monthOverMonthMRR.isPositive ? (
            <li>Monthly revenue growth is trending upward ({comparisonUtils.formatChangePercent(monthOverMonthMRR)})</li>
          ) : (
            <li>Monthly revenue growth has slowed ({comparisonUtils.formatChangePercent(monthOverMonthMRR)})</li>
          )}
          <li>Annual run rate: {founderAnalyticsService.formatCurrency(annualRunRate)}</li>
          {mrr.subscription_count > 0 && (
            <li>Average revenue per subscription: {founderAnalyticsService.formatCurrency(mrr.current_mrr / mrr.subscription_count)}</li>
          )}
        </ul>
      </div>
    </div>
  );
}
